import React, { useState } from 'react'
import { Amplify } from 'aws-amplify'
import { useNavigate } from 'react-router-dom'
import { useFeatures } from 'flagged'
import { useDispatch } from 'react-redux'
import { useTheme } from '../../../../components/mui/Styles/index'
import {
  Backdrop,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  TextField,
} from '../../../../components/mui/Core/index'
import FormHeading from '../../../../components/userauthentication/FormHeading'
import { confirmSignUp } from '../../../../api/userauthentication/signup'
import awsconfig from '../../../../aws-exports'
import { businessMessages } from '../../../../constants/business-message-constants'
import GenericSnackbar from '../../../../components/GenericSnackbar/generic-snackbar'
import Constants from '../../../../constants/en.json'
import { confirmInvite } from '../../../../api/user/confirm-invite'
import useBrowserTabTitles from '../../../../hooks/browserTabTitles/browser-tab-titles'
import { signupChallengeTypes, signupErrorCodes } from '../../../../constants/sign-up-constants'
import { setSnackbar } from '../../../../components/GenericSnackbar/generic-snackbar-slice'

Amplify.configure(awsconfig)

// Confirm Sign Up Form -/signup
function ConfirmInvite() {
  useBrowserTabTitles(Constants.confirmInvite.PAGE_TITLE)
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const theme = useTheme()
  const features = useFeatures()
  const [isLoading, setIsLoading] = useState(false)
  const isUserServiceEnabled = features.isUserServiceEnabled
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [emailAddress, setEmailAddress] = useState('')
  const [temporaryPassword, setTemporaryPassword] = useState('')

  const isFormValid =
    (!isUserServiceEnabled && !firstName) ||
    (!isUserServiceEnabled && !lastName) ||
    !emailAddress ||
    !temporaryPassword

  const handleConfirmInvite = () => {
    if (!isUserServiceEnabled) {
      setIsLoading(true)
      const request = {
        username: emailAddress,
        firstName,
        lastName,
        email: emailAddress,
        code: temporaryPassword,
      }
      confirmSignUp(request)
        .then(() => {
          /* Nothing to do here because aws will always return NotAuthorizedException exception with
        user status as "FORCE_CHANGE_PASSWORD". */
        })
        .catch((exception) => {
          /* When confirming invite with default password, AWS will
           throw exception and require to reset the password.  */
          if (exception?.message.includes(signupChallengeTypes.FORCE_CHANGE_PASSWORD)) {
            /* Take the user to change password page to enter new password */
            navigate(`/-signup/profile-setup`, { state: request })
          }
          /* If the user is already confirmed, display already confirmed message */
          if (exception?.message.includes('CONFIRMED')) {
            dispatch(
              setSnackbar({
                message: businessMessages.confirmInvite.USER_ALREADY_CONFIRMED,
                severity: 'error',
                action: true,
              }),
            )
          }
          setIsLoading(false)
        })
    }

    if (isUserServiceEnabled) {
      setIsLoading(true)
      const request = {
        username: emailAddress,
        password: temporaryPassword,
      }

      confirmInvite(request)
        .then((response) => {
          if (response.data.data.challengeType === signupChallengeTypes.NEW_PASSWORD_REQUIRED) {
            // TODO: Refactor with new screen to set first and last name
            const profileSetupState = {
              username: emailAddress,
              email: emailAddress,
              code: temporaryPassword,
              confirmedUserSessionId: response.data.data.sessionId,
              challengeType: signupChallengeTypes.NEW_PASSWORD_REQUIRED,
              challengeName: signupChallengeTypes.NEW_PASSWORD_REQUIRED,
            }
            navigate('/-signup/profile-setup', { state: profileSetupState })
          }
        })
        .catch((exception) => {
          if (exception?.code === signupErrorCodes.TEMPORARY_PASSWORD_EXPIRED) {
            // TODO: When user temp password expires, redirect to reinvite page.
            navigate('/-signup/temporary-password-expired')
          } else {
            dispatch(
              setSnackbar({
                message: exception?.message,
                severity: 'error',
                action: true,
              }),
            )
          }
          setIsLoading(false)
        })
    }
  }

  const handleOnEmailChange = (event) => {
    setEmailAddress(event.target.value)
  }

  const handleOnFirstNameChange = (event) => {
    setFirstName(event.target.value)
  }

  const handleOnLastNameChange = (event) => {
    setLastName(event.target.value)
  }

  const handleOnTemporaryPasswordChange = (event) => {
    setTemporaryPassword(event.target.value)
  }

  const cardContentStyle = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
  }

  return (
    <Box sx={theme.soloContainer}>
      <Backdrop open={isLoading}>
        <CircularProgress color='inherit' />
      </Backdrop>
      <Card className='soloCard'>
        <CardContent sx={cardContentStyle}>
          <FormHeading formHeader={Constants.confirmInvite.CONFIRM_YOUR_INVITE} />
          <TextField
            onChange={handleOnEmailChange}
            required
            id='outlined-basic-email'
            label={Constants.EMAIL}
            variant='outlined'
            type='email'
            sx={{ width: '240px' }}
          />
          {!isUserServiceEnabled && (
            <>
              <TextField
                onChange={handleOnFirstNameChange}
                required
                id='outlined-basic-first-name'
                label={Constants.FIRST_NAME}
                variant='outlined'
                type='text'
                sx={{ width: '240px' }}
              />
              <TextField
                onChange={handleOnLastNameChange}
                required
                id='outlined-basic-last-name'
                label={Constants.LAST_NAME}
                variant='outlined'
                type='text'
                sx={{ width: '240px' }}
              />
            </>
          )}
          <TextField
            onChange={handleOnTemporaryPasswordChange}
            required
            id='outlined-basic-temp-password'
            label={Constants.confirmInvite.TEMPORARY_PASSWORD}
            variant='outlined'
            type='text'
            sx={{ width: '240px' }}
          />
          <Button
            onClick={handleConfirmInvite}
            variant='contained'
            sx={{ mt: 3 }}
            disabled={isFormValid || isLoading}
          >
            {Constants.confirmInvite.CONFIRM_INVITE}
          </Button>
        </CardContent>
      </Card>
      <GenericSnackbar />
    </Box>
  )
}

export default ConfirmInvite
